/**
 * Deterministic result-slot resolution for the Quiz component.
 *
 * The results view renders three card slots. Each slot may be informed by one
 * or more questions — a "weighted slot" — and the answers to those questions
 * select exactly one authored card variant. Selection is arithmetic, so there
 * is no voting, no tie-breaking, and no default ending.
 */

/** A single authored result card. */
export interface QuizResultCard {
  heading?: string;
  description?: string;
  imageUrl?: string;
  imageAlt?: string;
}

/**
 * One question the quiz actually asks, as the results view sees it.
 *
 * `questionNumber` is the `question{N}` prop the author filled in, which is what
 * a slot mapping names. It is carried explicitly because authors may leave gaps
 * — filling `question1`, `question2` and `question4` — and the quiz then asks
 * three questions whose positions no longer match their prop numbers.
 */
export interface QuizAnsweredQuestion {
  /** 1-based `question{N}` prop this question was authored in. */
  questionNumber: number;
  /**
   * How many answer addresses this question spans — its highest filled `Answer{M}`
   * slot. This is the radix the variant order counts in, so an answer's `Answer{M}`
   * number is its digit and a blank slot below a filled one still occupies an
   * address rather than shifting later answers down.
   */
  answerSlots: number;
  /** The visitor's selected answer index, or `undefined` if unanswered. */
  answer?: number;
}

export interface ResolveResultSlotInput {
  /** Comma-separated `question{N}` prop numbers informing this slot, e.g. `"1,4"`. */
  mapping?: string;
  /** Card variants authored for this slot, positionally ordered from variant 1. */
  variants: QuizResultCard[];
  /** The questions the quiz asks, in the order it asks them. */
  questions: QuizAnsweredQuestion[];
  /** 1-based slot number, used in authoring warnings. */
  slotNumber: number;
}

const isAuthored = (card: QuizResultCard | undefined): card is QuizResultCard =>
  Boolean(card && (card.heading || card.description || card.imageUrl));

/**
 * What an author's slot mapping asked for, and what of it the quiz can deliver.
 *
 * `requested` is carried separately from `positions` because an empty
 * `positions` has two very different causes: the author mapped nothing, or the
 * author mapped questions this quiz does not ask. The first is the documented
 * static-card path; the second is an authoring error.
 */
export interface QuestionMapping {
  /**
   * Whether the author supplied any mapping token at all, valid or not.
   *
   * Deliberately loose, and kept separate from `addressable`: this flag chooses
   * between the static-card path and an authoring error, so narrowing it to
   * plausible question numbers would send `"0"` or `"-2"` down the static path and
   * reinstate the fallback this module does not have.
   */
  authored: boolean;
  /**
   * How many distinct questions the mapping could address — distinct positive
   * integers.
   *
   * The variant order is sized against this rather than against every token, so a
   * malformed token is never reported as a question dropped from the address space.
   */
  addressable: number;
  /** Positions in `questions`, in author order, for the names that resolved. */
  positions: number[];
}

/** Why a slot's mapped answers could not select a variant. */
export type VariantUnresolvedReason =
  | 'question-not-asked'
  | 'no-authored-answers'
  | 'no-answer-selected'
  | 'answer-out-of-range';

/** A slot's mapped answers selected this variant position. */
export interface ResolvedVariant {
  resolved: true;
  position: number;
}

/** A slot's mapped answers selected nothing, and why. */
export interface UnresolvedVariant {
  resolved: false;
  reason: VariantUnresolvedReason;
  /** The `question{N}` prop at fault, or `-1` when the position was not an asked question. */
  questionNumber: number;
}

/**
 * The outcome of addressing a slot's variant order with the visitor's answers.
 *
 * The unresolved case names its own cause so the caller can tell an author which
 * of four unrelated problems it hit.
 */
export type VariantResolution = ResolvedVariant | UnresolvedVariant;

/**
 * Narrows a resolution to its unresolved arm.
 *
 * Written as an explicit guard rather than relying on `!resolution.resolved` to
 * narrow: `scripts/build-registry.mjs` type-checks this source through a bare
 * `tsc` with no tsconfig, so `strictNullChecks` is off there and a boolean-literal
 * discriminant does not narrow. A guard narrows under either setting.
 *
 * Fixing the build script so both passes agree is tracked in NEMO-5834.
 */
const isUnresolved = (resolution: VariantResolution): resolution is UnresolvedVariant =>
  !resolution.resolved;

/**
 * How many distinct combinations of answer slots a slot's mapped questions span.
 *
 * Blank answer slots are counted, because they still occupy an address in the
 * variant order.
 */
const countCombinations = (
  mappedQuestions: number[],
  questions: QuizAnsweredQuestion[]
): number =>
  mappedQuestions.reduce((total, position) => total * questions[position].answerSlots, 1);

/**
 * Warns when a card that is about to render carries an image with no alt text.
 *
 * The card still renders — `Quiz` falls back to `alt=""`, which is correct for a
 * decorative image — but an informative image needs a description, and only the
 * author can tell the two apart.
 */
const warnOnMissingAltText = (card: QuizResultCard, slotNumber: number): void => {
  if (process.env.NODE_ENV === 'production') return;

  if (card.imageUrl && !card.imageAlt) {
    console.warn(
      `[Quiz] Result card ${slotNumber} renders an image with no alt text. ` +
        `Describe the image, or set its alt text to an empty string if it is decorative.`
    );
  }
};

/**
 * Parses an author-supplied slot mapping into positions in `questions`.
 *
 * A mapping names `question{N}` prop numbers, so each token is matched against
 * `questionNumber` rather than treated as a position. That keeps a mapping of
 * `"4"` pointing at `question4` even when an earlier question slot is blank. A
 * repeated question collapses to its first position so it contributes to the
 * combination only once.
 *
 * Any token that does not name a question the quiz asks — a typo, a fraction, a
 * question that was never authored — matches nothing and is dropped with a
 * warning, so parsing needs no separate numeric validation.
 *
 * @param mapping comma-separated `question{N}` prop numbers
 * @param questions the questions the quiz asks, in the order it asks them
 * @returns how many distinct questions were named, and the positions that resolved
 */
export const parseQuestionMapping = (
  mapping: string | undefined,
  questions: QuizAnsweredQuestion[]
): QuestionMapping => {
  if (!mapping) return { authored: false, addressable: 0, positions: [] };

  const positions: number[] = [];
  // Counted by question number rather than by token so `"1,1"` sizes the address
  // space at one question, not two with one of them dropped.
  const addressableNumbers = new Set<number>();
  let authored = false;

  for (const token of mapping.split(',')) {
    const trimmed = token.trim();
    if (!trimmed) continue;

    authored = true;

    const questionNumber = Number(trimmed);
    if (Number.isInteger(questionNumber) && questionNumber > 0) {
      addressableNumbers.add(questionNumber);
    }

    const position = questions.findIndex(
      question => question.questionNumber === questionNumber
    );

    if (position === -1) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn(
          `[Quiz] A result card maps to question "${trimmed}", which this quiz does not ask. Ignoring it.`
        );
      }
      continue;
    }

    if (!positions.includes(position)) {
      positions.push(position);
    }
  }

  return { authored, addressable: addressableNumbers.size, positions };
};

/**
 * Resolves which variant position a slot's mapped answers select.
 *
 * The mapped questions act as digits of a mixed-radix number: the first mapped
 * question is the most significant, so the last mapped question varies fastest
 * across the authored variant order. Each digit is the answer's `Answer{M}` prop
 * number, counted in that question's answer-slot radix.
 *
 * @returns the 0-based variant position, or the reason no variant could be selected
 */
export const resolveVariantIndex = (
  mappedQuestions: number[],
  questions: QuizAnsweredQuestion[]
): VariantResolution => {
  let position = 0;

  for (const questionPosition of mappedQuestions) {
    const question = questions[questionPosition];
    if (!question) {
      return { resolved: false, reason: 'question-not-asked', questionNumber: -1 };
    }

    const { answerSlots, answer, questionNumber } = question;

    if (!answerSlots) {
      return { resolved: false, reason: 'no-authored-answers', questionNumber };
    }

    if (answer === undefined) {
      return { resolved: false, reason: 'no-answer-selected', questionNumber };
    }

    if (answer < 0 || answer >= answerSlots) {
      return { resolved: false, reason: 'answer-out-of-range', questionNumber };
    }

    position = position * answerSlots + answer;
  }

  return { resolved: true, position };
};

/** What to tell an author about each way a slot's answers can fail to select a variant. */
const UNRESOLVED_VARIANT_CAUSE: Record<
  VariantUnresolvedReason,
  (questionNumber: number) => string
> = {
  'question-not-asked': () => 'maps to a question this quiz does not ask',
  'no-authored-answers': questionNumber =>
    `maps to question ${questionNumber}, which has no answers authored`,
  'no-answer-selected': questionNumber =>
    `maps to question ${questionNumber}, which the visitor has not answered`,
  'answer-out-of-range': questionNumber =>
    `maps to question ${questionNumber}, whose recorded answer is outside that question's answer slots`,
};

/**
 * Guidance appended after the outcome, for the causes that have a specific remedy.
 *
 * Kept out of the cause text so each message reads cause, then outcome, then
 * explanation — an aside placed before the outcome ends up owning it.
 */
const UNRESOLVED_VARIANT_ADVICE: Partial<Record<VariantUnresolvedReason, string>> = {
  'no-answer-selected':
    ' A controlled quiz must pass `selectedAnswers` alongside `currentQuestion` — it never ' +
    'captures answers on its own.',
};

/**
 * Picks the card for a slot the author never mapped, which shows the same variant
 * to every quiz-taker and keeps quizzes authored before weighted slots unchanged.
 *
 * Variants beyond the first cannot render in this state. That is worth saying out
 * loud: the mapping is one non-content field among the slot's twenty-five, and
 * forgetting it looks identical to deliberately authoring a static card.
 */
const resolveStaticSlot = (
  variants: QuizResultCard[],
  slotNumber: number
): QuizResultCard | undefined => {
  const baseCard = variants[0];

  if (process.env.NODE_ENV !== 'production' && variants.slice(1).some(isAuthored)) {
    // The remedy holds either way — a mapping is what makes a later variant
    // reachable — but the diagnosis has to name the outcome the author will see.
    const outcome = isAuthored(baseCard)
      ? 'only variant 1 can ever render'
      : 'nothing can render';

    console.warn(
      `[Quiz] Result card ${slotNumber} has extra variants authored but names no questions, ` +
        `so ${outcome}. Set resultCard${slotNumber}Questions to the question numbers that ` +
        `should select between them.`
    );
  }

  if (!isAuthored(baseCard)) return undefined;

  warnOnMissingAltText(baseCard, slotNumber);
  return baseCard;
};

/**
 * Warns that a mapping named only questions this quiz does not ask.
 *
 * Falling back to variant 1 here would be a default ending, which this module
 * does not have, so the slot renders nothing and the author is told which of the
 * two states they are in.
 */
const warnMappingResolvedNothing = (slotNumber: number): void => {
  if (process.env.NODE_ENV === 'production') return;

  console.warn(
    `[Quiz] Result card ${slotNumber} names only questions this quiz does not ask, so it renders ` +
      `no card. Point resultCard${slotNumber}Questions at questions the quiz asks, or clear it to ` +
      `show variant 1 as a static card.`
  );
};

/**
 * Warns that only some of a mapping's questions resolved.
 *
 * The survivors still address the variant order, but in a narrower radix than the
 * author laid out — so variants past the new ceiling can never be reached, and the
 * ones below it now stand for different answers than intended.
 */
const warnAddressSpaceRebased = (
  slotNumber: number,
  requested: number,
  resolved: number
): void => {
  if (process.env.NODE_ENV === 'production') return;

  console.warn(
    `[Quiz] Result card ${slotNumber} names ${requested} questions but only ${resolved} of them are ` +
      `asked, so its variants are now addressed by ${resolved}. Variants authored for the wider ` +
      `mapping can no longer be reached — fix the mapping, or renumber the variants to match.`
  );
};

/**
 * Picks the card a result slot renders.
 *
 * Three authoring states reach this function and each gets its own outcome: a slot
 * that names no questions shows variant 1 to everyone, a slot whose named questions
 * are none of the ones asked renders nothing, and a slot where only some resolved
 * renders against the narrower address space with a warning.
 *
 * A mapping wider than the slot's variant capacity is reported on every results
 * render rather than only when a visitor happens to land on an unauthored
 * combination, so the misconfiguration surfaces on first preview.
 */
export const resolveResultSlot = ({
  mapping,
  variants,
  questions,
  slotNumber,
}: ResolveResultSlotInput): QuizResultCard | undefined => {
  const { authored, addressable, positions } = parseQuestionMapping(mapping, questions);

  if (!authored) {
    return resolveStaticSlot(variants, slotNumber);
  }

  if (positions.length === 0) {
    warnMappingResolvedNothing(slotNumber);
    return undefined;
  }

  if (positions.length < addressable) {
    warnAddressSpaceRebased(slotNumber, addressable, positions.length);
  }

  const combinationCount = countCombinations(positions, questions);
  const authorableVariants = variants.length;
  const mappingExceedsCapacity = combinationCount > authorableVariants;

  if (mappingExceedsCapacity) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        `[Quiz] Result card ${slotNumber} maps to questions with ${combinationCount} answer combination(s), ` +
          `but the slot can only author ${authorableVariants}. Combinations beyond variant ` +
          `${authorableVariants} render no card — map fewer questions, or questions with fewer answers.`
      );
    }
  }

  const resolution = resolveVariantIndex(positions, questions);
  if (isUnresolved(resolution)) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        `[Quiz] Result card ${slotNumber} ` +
          `${UNRESOLVED_VARIANT_CAUSE[resolution.reason](resolution.questionNumber)}, ` +
          `so it renders no card.` +
          (UNRESOLVED_VARIANT_ADVICE[resolution.reason] ?? '')
      );
    }
    return undefined;
  }

  const variantPosition = resolution.position;
  const card = variants[variantPosition];
  if (!isAuthored(card)) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        `[Quiz] Result card ${slotNumber} has no variant ${variantPosition + 1} authored, ` +
          `so the selected answers render no card.` +
          // Only advise authoring the full set when the author can actually supply it.
          (mappingExceedsCapacity
            ? ''
            : ` Author all ${combinationCount} variant(s) so every combination resolves.`)
      );
    }
    return undefined;
  }

  warnOnMissingAltText(card, slotNumber);
  return card;
};
